import { z } from 'zod'
import type { Interval } from '@/shared/lib'

/**
 * Интервал на проводе — доменный `shared.TimeSlot`. Время уходит и приходит в
 * RFC3339 UTC, внутри интерфейса живёт `Interval` из `Date`: экземпляр
 * обслуживает одну команду в одной зоне, поэтому «день» считается по местному
 * времени.
 *
 * Провод — `snake_case`: так отдаёт бэкенд, и схемы описывают именно провод.
 * Переименование в доменную форму делают мапперы сущностей, а не схемы.
 */
export const SlotSchema = z.object({
  starts_at: z.iso.datetime(),
  ends_at: z.iso.datetime(),
})

export type SlotDto = z.infer<typeof SlotSchema>

export function toInterval(slot: SlotDto): Interval {
  return { startsAt: new Date(slot.starts_at), endsAt: new Date(slot.ends_at) }
}

/** Пара `starts_at`/`ends_at` на верхнем уровне тела — так их ждут выходы. */
export function toSlotFields(interval: Interval): { starts_at: string; ends_at: string } {
  return {
    starts_at: interval.startsAt.toISOString(),
    ends_at: interval.endsAt.toISOString(),
  }
}

export function toSlotDto(interval: Interval): SlotDto {
  return toSlotFields(interval)
}

/**
 * Пустая строка вместо `null`: Go отдаёт необязательные строки с `omitempty`,
 * поэтому «нет значения» приходит отсутствующим полем или пустой строкой.
 */
export const OptionalString = z
  .string()
  .optional()
  .transform((value) => (value ? value : null))

/**
 * Предупреждение о занятости — одна форма на всё API: накладка у исполнителя
 * дела, закрытое время человека и занятый гид выхода приходят одинаково. `kind`
 * говорит, чем именно занято время; `reason` заполнен только у закрытого времени
 * — у дела и выхода за него отвечает название. Живёт в shared, потому что
 * структуру делят три сущности.
 *
 * Пересечения разрешены: это предупреждение по факту прошедшей операции, а не
 * отказ (`schedule.ConflictWarning`).
 */
export const WarningSchema = z
  .object({
    employee_id: z.uuid(),
    kind: z.enum(['task', 'time_block', 'excursion']),
    id: z.uuid(),
    title: z.string(),
    reason: OptionalString,
    slot: SlotSchema,
  })
  .transform((dto) => ({
    employeeId: dto.employee_id,
    kind: dto.kind,
    id: dto.id,
    title: dto.title,
    reason: dto.reason,
    slot: toInterval(dto.slot),
  }))

export type Warning = z.infer<typeof WarningSchema>

/** Go отдаёт пустые срезы как `null` — приводим к массиву на границе. */
export function nullableArray<T extends z.ZodTypeAny>(item: T) {
  return z
    .array(item)
    .nullish()
    .transform((value) => value ?? [])
}

export const UuidSchema = z.uuid()
